import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import SpotifyPlayer from './SpotifyPlayer';
import SpotifySearch from './SpotifySearch';
import { useTranslation } from 'react-i18next';

const AmbientAudio = ({ isPlaying = false, onToggle }) => {
  const { t } = useTranslation();
  const [volume, setVolume] = useState(0.3);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [audioSource, setAudioSource] = useState('ambient'); // 'ambient' or 'spotify'
  const [spotifyDeviceId, setSpotifyDeviceId] = useState(null);
  const [spotifyPremium, setSpotifyPremium] = useState(false);
  const audioRef = useRef(null);

  // Royalty-free nature sounds from Pixabay (CDN-hosted, free for commercial use)
  const audioTracks = [
    {
      name: "Forest Rain",
      url: "https://cdn.pixabay.com/audio/2022/06/07/audio_b9bd35e7a4.mp3",
      icon: "🌧️",
      fallback: false
    },
    {
      name: "Ocean Waves",
      url: "https://cdn.pixabay.com/audio/2022/03/15/audio_c8e7a52d8a.mp3",
      icon: "🌊",
      fallback: false
    },
    {
      name: "Forest Birds",
      url: "https://cdn.pixabay.com/audio/2022/10/30/audio_347111d654.mp3",
      icon: "🍃",
      fallback: false
    }
  ];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = volume;
    audio.loop = true;

    if (isPlaying) {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Auto-play failed, user needs to interact first
          console.log('Audio autoplay failed - user interaction required');
        });
      }
    } else {
      audio.pause();
    }
  }, [isPlaying, volume, currentTrack]);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const switchTrack = (index) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentTrack(index);
  };

  const handleSpotifyReady = (deviceId) => {
    setSpotifyDeviceId(deviceId);
    setSpotifyPremium(true);
  };

  const handleTrackSelect = (track) => {
    console.log('Selected track:', track);
  };

  return (
    <div className="space-y-4">
      {/* Audio Source Selector */}
      <Card className="glass-card p-4">
        <h3 className="font-medium text-gray-800 text-sm mb-3">{t('ambient.title')}</h3>
        <div className="flex space-x-2">
          <Button
            onClick={() => setAudioSource('ambient')}
            variant={audioSource === 'ambient' ? 'default' : 'outline'}
            className={audioSource === 'ambient' ? 'btn-primary' : 'btn-secondary'}
            size="sm"
          >
            🌿 {t('ambient.ambientTab')}
          </Button>
          <Button
            onClick={() => setAudioSource('spotify')}
            variant={audioSource === 'spotify' ? 'default' : 'outline'}
            className={audioSource === 'spotify' ? 'btn-primary' : 'btn-secondary'}
            size="sm"
          >
            🎵 {t('ambient.spotifyTab')}
          </Button>
        </div>
      </Card>

      {/* Ambient Audio Controls */}
      {audioSource === 'ambient' && (
        <Card className="glass-card p-4" data-testid="ambient-audio-controls">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-800 text-sm">🎵 {t('ambient.ambientTab')}</h3>
            <Button
              onClick={onToggle}
              variant={isPlaying ? "default" : "outline"}
              size="sm"
              className={isPlaying ? "btn-primary" : "btn-secondary"}
              data-testid="toggle-audio-btn"
            >
              {isPlaying ? `⏸️ ${t('ambient.pause')}` : `▶️ ${t('ambient.play')}`}
            </Button>
          </div>
          
          {/* Track Selection */}
          <div className="flex gap-2 mb-3">
            {audioTracks.map((track, index) => (
              <Button
                key={index}
                onClick={() => switchTrack(index)}
                variant={currentTrack === index ? "default" : "outline"}
                size="sm"
                className="text-xs p-1 h-8"
                data-testid={`track-${index}-btn`}
              >
                {track.icon}
              </Button>
            ))}
          </div>
          
          {/* Volume Control */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600">🔊</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              data-testid="volume-slider"
            />
            <span className="text-xs text-gray-500 w-8">{Math.round(volume * 100)}%</span>
          </div>
          
          {/* Audio Element */}
          <audio
            ref={audioRef}
            src={audioTracks[currentTrack].url}
            preload="metadata"
          />
          
          {/* Current Track Display */}
          <div className="mt-2 text-center">
            <span className="text-xs text-gray-500">
              {audioTracks[currentTrack].name}
            </span>
          </div>
        </Card>
      )}

      {/* Spotify Integration */}
      {audioSource === 'spotify' && (
        <>
          <SpotifyPlayer 
            onReady={handleSpotifyReady}
            onPlayerStateChanged={(state) => console.log('Player state:', state)}
          />
          
          <SpotifySearch 
            deviceId={spotifyDeviceId}
            isPremium={spotifyPremium}
            onTrackSelect={handleTrackSelect}
          />
        </>
      )}
    </div>
  );
};

export default AmbientAudio;
